import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Network } from "@capacitor/network";
import { setOnline as setGlobalOnline } from "lib/networkState";

function readBrowserOnline(): boolean {
    return typeof navigator !== "undefined" ? navigator.onLine : true;
}

/**
 * Subscribes to network status and keeps {@link lib/networkState} in sync for apiClient.
 */
export function useOnline(): { isOnline: boolean } {
    const [isOnline, setIsOnline] = useState(readBrowserOnline);

    useEffect(() => {
        let cancelled = false;

        const apply = (online: boolean) => {
            if (cancelled) return;
            setGlobalOnline(online);
            setIsOnline(online);
        };

        if (Capacitor.isNativePlatform()) {
            void Network.getStatus().then((status) => {
                apply(status.connected);
            }).catch(() => {
                apply(readBrowserOnline());
            });

            let handle: { remove: () => Promise<void> } | undefined;
            void Network.addListener("networkStatusChange", (status) => {
                apply(status.connected);
            }).then((h) => {
                handle = h;
            });

            return () => {
                cancelled = true;
                void handle?.remove();
            };
        }

        apply(readBrowserOnline());
        const onOnline = () => apply(true);
        const onOffline = () => apply(false);
        window.addEventListener("online", onOnline);
        window.addEventListener("offline", onOffline);

        return () => {
            cancelled = true;
            window.removeEventListener("online", onOnline);
            window.removeEventListener("offline", onOffline);
        };
    }, []);

    return { isOnline };
}
